import { Chip, Tooltip } from "@mui/material"
import dayjs from "dayjs"
import type { Maybe } from "graphql/jsutils/Maybe"
import type React from "react"
import { twMerge } from "tailwind-merge"

import { DATE_FORMAT } from "@/constants/formats"
import { OrderStatus } from "@/graphql/codegen/graphql"

export interface GetStatusDisplayPropertiesParams {
  status?: Maybe<OrderStatus>
}

export type StatusChipColor = "warning" | "success" | "error" | "default" | "primary" | "secondary" | "info" | undefined

export interface StatusDisplayProperties {
  color: StatusChipColor
  label: string | null
}

export type GetStatusDisplayProperties = (params: GetStatusDisplayPropertiesParams) => StatusDisplayProperties

const getStatusDisplayProperties: GetStatusDisplayProperties = ({ status }) => {
  switch (status) {
    case OrderStatus.Cancelled:
      return {
        color: "error",
        label: "Cancelled",
      }
    case OrderStatus.Draft:
      return {
        color: "warning",
        label: "Draft",
      }
    case OrderStatus.Quote:
      return {
        color: "warning",
        label: "Quote",
      }
    case OrderStatus.CanceledQuote:
      return {
        color: "error",
        label: "Cancelled Quote",
      }
    case OrderStatus.Sent:
      return {
        color: "success",
        label: "Sent",
      }
    case OrderStatus.Confirmed:
      return {
        color: "primary",
        label: "Confirmed",
      }
    case OrderStatus.Completed:
      return {
        color: "primary",
        label: "Completed",
      }
    case OrderStatus.Shipped:
      return {
        color: "primary",
        label: "Shipped",
      }
    case OrderStatus.Received:
      return {
        color: "primary",
        label: "Received",
      }
    case OrderStatus.PartiallyReceived:
      return {
        color: "primary",
        label: "Partially Received",
      }
    case OrderStatus.PartiallyShipped:
      return {
        color: "primary",
        label: "Partially Shipped",
      }

    default:
      return {
        color: undefined,
        label: null,
      }
  }
}

interface OrderStatusChipProps {
  status?: Maybe<OrderStatus>
  convertedToQuoteOn?: Maybe<Date | string>
  className?: string
}

export const OrderStatusChip: React.FC<OrderStatusChipProps> = ({ status, convertedToQuoteOn, className, ...rest }) => {
  const { color, label } = getStatusDisplayProperties({ status })

  const chip = (
    <Chip
      {...rest}
      size='small'
      className={twMerge("bg-transparent text-xs text-gray-800", className)}
      variant='outlined'
      color={color}
      label={label}
    />
  )

  // Show tooltip for Quote status with convertedToQuoteOn date
  if (status === OrderStatus.Quote && convertedToQuoteOn && dayjs(convertedToQuoteOn).isValid()) {
    const formattedDate = dayjs(convertedToQuoteOn).format(DATE_FORMAT.US_SHORT)
    return (
      !!label && (
        <Tooltip title={`On ${formattedDate}`} arrow placement='top'>
          {chip}
        </Tooltip>
      )
    )
  }

  return !!label && chip
}
